import { TypedDocumentNode } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  BulkAddVisitorsToVisitorGroupsInput,
  Mutation,
  MutationBulkAddVisitorsToVisitorGroupsArgs,
  MutationBulkAssignVisitorRolesArgs,
  MutationBulkDeleteVisitorsArgs,
  Role,
  VisitorRole
} from 'graphql/generated/graphqlTypes';
import {
  BULK_ADD_VISITORS_TO_VISITOR_GROUPS,
  BULK_ASSIGN_VISITOR_ROLES,
  BULK_DELETE_VISITORS
} from 'graphql/v2/mutations';
import { GET_ALL_VISITORS_GROUPS } from 'graphql/v2/queries';
import { TranslatedHookProps } from 'v2/hooks/hooks.types';
import { refetchQueries } from 'v2/hooks/useAllVisitors/useAllVisitors';

const BULK_ADD_VISITORS_TO_VISITOR_GROUPS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'bulkAddVisitorsToVisitorGroups'>,
  MutationBulkAddVisitorsToVisitorGroupsArgs
> = BULK_ADD_VISITORS_TO_VISITOR_GROUPS;
const BULK_ASSIGN_VISITOR_ROLES_TYPED: TypedDocumentNode<
  Pick<Mutation, 'bulkAssignVisitorRoles'>,
  MutationBulkAssignVisitorRolesArgs
> = BULK_ASSIGN_VISITOR_ROLES;
const BULK_DELETE_VISITORS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'bulkDeleteVisitors'>,
  MutationBulkDeleteVisitorsArgs
> = BULK_DELETE_VISITORS;

interface BulkAddVisitorsToVisitorGroupsArgs {
  visitorIds: string[];
  visitorGroupIds: string[];
}

interface BulkAssignVisitorRolesArgs {
  visitorIds: string[];
  roles: VisitorRole[];
}

interface BulkDeleteVisitorsArgs {
  visitorIds: string[];
}

const createBulkAddVisitorsToVisitorGroupsInput = (
  visitorIds: string[],
  visitorGroupIds: string[]
): BulkAddVisitorsToVisitorGroupsInput => ({
  visitorIds,
  memberships: visitorGroupIds.map((visitorGroupId) => ({ visitorGroupId, role: Role.None }))
});

const useBulkUserActions = ({ handleFetchError, translation }: TranslatedHookProps) => {
  const [bulkAddVisitorsToVisitorGroupsMutation, { loading: bulkAddVisitorsToVisitorGroupsLoading }] = useMutation(
    BULK_ADD_VISITORS_TO_VISITOR_GROUPS_TYPED,
    {
      refetchQueries: [...refetchQueries, { query: GET_ALL_VISITORS_GROUPS }],
      awaitRefetchQueries: true,
      onError: () => handleFetchError(translation.users_add_to_groups_error)
    }
  );

  const bulkAddVisitorsToVisitorGroups = async ({
    visitorIds,
    visitorGroupIds
  }: BulkAddVisitorsToVisitorGroupsArgs) => {
    try {
      const { data } = await bulkAddVisitorsToVisitorGroupsMutation({
        variables: {
          input: createBulkAddVisitorsToVisitorGroupsInput(visitorIds, visitorGroupIds)
        }
      });

      return data?.bulkAddVisitorsToVisitorGroups;
    } catch {
      return undefined;
    }
  };

  const [bulkAssignVisitorRolesMutation, { loading: bulkAssignVisitorRolesLoading }] = useMutation(
    BULK_ASSIGN_VISITOR_ROLES_TYPED,
    {
      refetchQueries: [...refetchQueries, { query: GET_ALL_VISITORS_GROUPS }],
      awaitRefetchQueries: true,
      onError: () => handleFetchError(translation.users_assign_roles_error)
    }
  );

  const bulkAssignVisitorRoles = async ({ visitorIds, roles }: BulkAssignVisitorRolesArgs) => {
    try {
      const { data } = await bulkAssignVisitorRolesMutation({
        variables: { input: { visitorIds, roles } }
      });

      return data?.bulkAssignVisitorRoles;
    } catch {
      return undefined;
    }
  };

  const [bulkDeleteVisitorsMutation, { loading: bulkDeleteVisitorsLoading }] = useMutation(BULK_DELETE_VISITORS_TYPED, {
    refetchQueries: [...refetchQueries, { query: GET_ALL_VISITORS_GROUPS }],
    awaitRefetchQueries: true,
    onError: () => handleFetchError(translation.users_delete_error)
  });

  const bulkDeleteVisitors = async ({ visitorIds }: BulkDeleteVisitorsArgs) => {
    try {
      const { data } = await bulkDeleteVisitorsMutation({ variables: { input: { visitorIds } } });

      return data?.bulkDeleteVisitors;
    } catch {
      return undefined;
    }
  };

  return {
    bulkAddVisitorsToVisitorGroups,
    bulkAddVisitorsToVisitorGroupsLoading,
    bulkAssignVisitorRoles,
    bulkAssignVisitorRolesLoading,
    bulkDeleteVisitors,
    bulkDeleteVisitorsLoading
  };
};

export default useBulkUserActions;
